import React, { useState, useEffect } from 'react';
import { ChevronLeft, Clock, CheckCircle, FileText, Shield, AlertCircle } from 'lucide-react';

export default function ContractReadPage({ mode, contractType, isFromSupplierConfirm, onBack, onAgree }) {
  const [readTime, setReadTime] = useState(5);
  const [canAgree, setCanAgree] = useState(false);
  const [readProgress, setReadProgress] = useState({ purchase: false, service: false });

  const isOtherMode = mode === 'other';
  const isFromSupplier = isFromSupplierConfirm;

  useEffect(() => {
    const timer = setInterval(() => {
      setReadTime(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanAgree(true);
          if (isOtherMode) {
            setReadProgress({ purchase: true, service: true });
          } else {
            setReadProgress({ purchase: true, service: true });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOtherMode]);

  const contracts = [
    {
      type: 'purchase',
      title: '废钢采购合同',
      subtitle: '为保障交易安全，请仔细阅读合同条款',
      icon: Shield,
      color: 'from-blue-500 to-blue-600',
      sections: [
        {
          title: '第一条 采购标的',
          content: '甲方（采购方）根据生产需要，向乙方（供货方）采购符合本合同约定的废钢物资。'
        },
        {
          title: '第二条 质量标准',
          content: '乙方供应的废钢物资应符合国家相关质量标准，不得混入杂质。具体质量要求以甲方基地公示的标准为准。'
        },
        {
          title: '第三条 交货方式',
          content: '乙方应按照约定时间将废钢物资运送至甲方指定基地，并配合甲方进行质量检验。'
        },
        {
          title: '第四条 验收标准',
          content: '甲方按照国家相关标准及基地公示的验收标准对废钢物资进行验收。验收合格后，双方签署验收单。'
        },
        {
          title: '第五条 结算方式',
          content: '验收合格当日，甲方按照约定单价及实际验收重量进行结算。款项将支付至乙方指定的收款账户。'
        },
        {
          title: '第六条 违约责任',
          content: '任何一方违反本合同约定，应承担相应的违约责任，并赔偿对方因此造成的损失。'
        },
        {
          title: '第七条 争议解决',
          content: '本合同履行过程中发生争议，双方应友好协商解决；协商不成的，可向甲方所在地人民法院提起诉讼。'
        },
        {
          title: '第八条 其他约定',
          content: '本合同自双方签署之日起生效，一式两份，双方各执一份，具有同等法律效力。'
        }
      ]
    },
    {
      type: 'service',
      title: '平台服务协议',
      subtitle: '为保障您的权益，请仔细阅读服务条款',
      icon: FileText,
      color: 'from-green-500 to-green-600',
      sections: [
        {
          title: '第一条 服务内容',
          content: '本平台为废钢交易提供信息发布、交易撮合、合同签署、结算支付等服务。'
        },
        {
          title: '第二条 用户注册',
          content: '用户注册时需提供真实、准确、完整的个人信息，并对所提供信息的真实性负责。'
        },
        {
          title: '第三条 账户安全',
          content: '用户应妥善保管账户信息，不得将账户转让、出借给他人使用。因用户保管不善导致的损失，由用户自行承担。'
        },
        {
          title: '第四条 交易规则',
          content: '用户在平台进行交易时，应遵守平台交易规则，不得进行虚假交易、欺诈等违法行为。'
        },
        {
          title: '第五条 信息保护',
          content: '平台将采取合理措施保护用户个人信息安全，未经用户同意，不会向第三方泄露用户信息。'
        },
        {
          title: '第六条 禁止行为',
          content: '用户不得利用平台进行违法活动，包括但不限于洗钱、诈骗、传播违法信息等。'
        },
        {
          title: '第七条 责任限制',
          content: '平台不对因不可抗力、网络故障等非平台原因导致的服务中断承担责任。'
        },
        {
          title: '第八条 协议变更',
          content: '平台有权根据业务发展需要修改本协议，修改后的协议将通过平台公告方式通知用户。'
        }
      ]
    }
  ];

  const displayContracts = (() => {
    if (isFromSupplier) {
      return contracts;
    }
    if (isOtherMode) {
      return contracts.filter(c => c.type === 'service');
    }
    if (contractType) {
      return contracts.filter(c => c.type === contractType);
    }
    return contracts;
  })();

  const completedCount = isOtherMode 
    ? (readProgress.service ? 1 : 0)
    : Object.values(readProgress).filter(Boolean).length;
  const totalCount = displayContracts.length;

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">阅读合同条款</span>
        <div className="ml-auto flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
          <span className="text-sm font-bold text-blue-600">{completedCount}/{totalCount}</span>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center gap-2 mb-1">
            <Shield size={16} />
            <span className="text-sm font-medium">重要提示</span>
          </div>
          <div className="font-bold text-xl">请仔细阅读合同条款</div>
          <div className="text-sm text-white/80 mt-1">阅读完成后即可签署电子合同</div>
        </div>

        {displayContracts.map((contract, index) => {
          const Icon = contract.icon;
          return (
            <div key={contract.type} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className={`bg-gradient-to-r ${contract.color} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Icon size={20} />
                    </div>
                    <div>
                      <div className="font-bold text-lg">{contract.title}</div>
                      <div className="text-xs text-white/80">{contract.subtitle}</div>
                    </div>
                  </div>
                  {readProgress[contract.type] && (
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                      <CheckCircle size={16} className="text-green-500" />
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  {contract.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                      <h4 className="font-bold text-slate-800 mb-2">{section.title}</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{section.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700 leading-relaxed">
              请仔细阅读合同条款，勾选同意即代表您已充分理解并接受合同内容，具有法律效力。
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white border-t border-slate-200 p-4 pointer-events-auto">
        {!canAgree ? (
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-600">
              <Clock size={16} className="text-orange-500" />
              <span className="text-sm">请仔细阅读合同内容</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-orange-600">{readTime}</span>
              <span className="text-sm text-slate-500">秒后可同意</span>
            </div>
          </div>
        ) : (
          <button
            onClick={onAgree}
            className="w-full py-3.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <CheckCircle size={20} />
            我已阅读并同意{isFromSupplier ? '全部合同' : isOtherMode ? '平台服务协议' : contractType === 'purchase' ? '废钢采购合同' : contractType === 'service' ? '平台服务协议' : '全部合同'}
          </button>
        )}
        </div>
      </div>
    </div>
  );
}
